import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, TrendingUp, ShoppingBag, Users, Store, Sparkles, CreditCard,
  Landmark, Plus, MonitorPlay, ArrowUpRight, Eye, Package, DollarSign, MessageCircle,
  Inbox, Settings as SettingsIcon, Loader2,
} from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { salesStats, revenueSeries, recentOrders } from '../mock';
import { usd } from '../lib/format';
import { API } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import BillingTab from '../components/dashboard/BillingTab';
import PayoutsTab from '../components/dashboard/PayoutsTab';
import ConciergeTab from '../components/dashboard/ConciergeTab';
import SocialTab from '../components/dashboard/SocialTab';
import LeadsTab from '../components/dashboard/LeadsTab';
import SettingsTab from '../components/dashboard/SettingsTab';
import { toast } from 'sonner';

const StatCard = ({ icon: Icon, label, value, sub, accent }) => (
  <div className="glass rounded-2xl p-4">
    <div className="flex items-center justify-between">
      <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/[0.06]">
        <Icon className={`h-4 w-4 ${accent ? 'text-primary' : 'text-white/70'}`} />
      </span>
      {sub && <span className="flex items-center gap-0.5 text-xs font-medium text-primary"><ArrowUpRight className="h-3 w-3" />{sub}</span>}
    </div>
    <p className="mt-3 font-display text-2xl font-bold text-white">{value}</p>
    <p className="text-xs text-muted-foreground">{label}</p>
  </div>
);

function Overview() {
  const max = Math.max(...revenueSeries.map((d) => d.value));
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard icon={DollarSign} label="Total revenue" value={usd(salesStats.revenueTotal)} sub="24%" accent />
        <StatCard icon={Users} label="Conversion rate" value={`${salesStats.conversion}%`} sub="1.2%" />
        <StatCard icon={Sparkles} label="AI inquiries handled" value={salesStats.aiInquiries.toLocaleString()} sub="31%" accent />
        <StatCard icon={ShoppingBag} label="Orders (30d)" value={salesStats.orders30d} sub="8%" />
      </div>

      <div className="grid gap-4 lg:grid-cols-5">
        {/* Chart */}
        <div className="glass rounded-2xl p-5 lg:col-span-3">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-display text-sm font-semibold text-white">Revenue this week</h3>
            <span className="text-xs text-muted-foreground">Total {usd(revenueSeries.reduce((a, b) => a + b.value, 0))}</span>
          </div>
          <div className="flex items-end justify-between gap-3">
            {revenueSeries.map((d) => (
              <div key={d.day} className="flex flex-1 flex-col items-center gap-2">
                <div className="flex h-40 w-full items-end">
                  <div
                    className="w-full rounded-t-lg bg-gradient-to-t from-primary/40 to-primary transition-luxe hover:from-primary/60"
                    style={{ height: `${(d.value / max) * 100}%` }}
                    title={usd(d.value)}
                  />
                </div>
                <span className="text-xs text-muted-foreground">{d.day}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Recent orders */}
        <div className="glass rounded-2xl p-5 lg:col-span-2">
          <h3 className="mb-3 font-display text-sm font-semibold text-white">Recent orders</h3>
          <div className="space-y-1">
            {recentOrders.slice(0, 6).map((o) => (
              <div key={o.id} className="flex items-center gap-3 rounded-lg px-1.5 py-2 hover:bg-white/[0.03]">
                <img src={o.avatar} alt={o.buyer} className="h-8 w-8 rounded-full object-cover" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-white">{o.buyer}</p>
                  <p className="truncate text-xs text-muted-foreground">{o.product}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold text-primary">{usd(o.amount)}</p>
                  <p className="text-xs text-muted-foreground">{o.time}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function ProductsTab() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ title: '', price: '', type: 'Coaching Call' });

  useEffect(() => {
    API.get('/products').then(({ data }) => setItems(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const add = async () => {
    if (!form.title || !form.price) { toast.error('Add a title and price'); return; }
    try {
      const { data } = await API.post('/products', {
        title: form.title, price: Number(form.price), type: form.type,
        subtitle: 'New offer', image: 'https://images.unsplash.com/photo-1526570207772-784d36084510',
        highlights: [], active: true,
      });
      setItems((prev) => [data, ...prev]);
      setForm({ title: '', price: '', type: 'Coaching Call' });
      setOpen(false);
      toast.success('Product added to your store');
    } catch { toast.error('Could not add product'); }
  };

  const toggle = async (p, v) => {
    setItems((prev) => prev.map((it) => it.product_id === p.product_id ? { ...it, active: v } : it));
    try { await API.put(`/products/${p.product_id}`, { ...p, active: v }); } catch { toast.error('Update failed'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-sm font-semibold text-white">Your products ({items.length})</h3>
        <Button onClick={() => setOpen(true)} size="sm" className="rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90 glow-lime">
          <Plus className="mr-1 h-4 w-4" /> Add product
        </Button>
      </div>

      {loading ? (
        <div className="glass rounded-2xl p-8 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-primary" /></div>
      ) : (
        <div className="space-y-3">
          {items.map((p) => (
            <div key={p.product_id} className="glass flex items-center gap-4 rounded-2xl p-3">
              <img src={p.image} alt={p.title} className="h-14 w-14 rounded-xl object-cover" />
              <div className="min-w-0 flex-1">
                <p className="truncate font-display text-sm font-semibold text-white">{p.title}</p>
                <p className="text-xs text-muted-foreground">{p.type}</p>
              </div>
              <span className="font-display text-sm font-bold text-white">{usd(p.price)}</span>
              <Switch checked={p.active} onCheckedChange={(v) => toggle(p, v)} className="data-[state=checked]:bg-primary" />
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="glass-strong max-w-md rounded-3xl border-white/10 text-white">
          <DialogHeader><DialogTitle className="font-display">Add a product</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="mb-1 block text-xs text-muted-foreground">Title</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. VIP Strategy Day" className="rounded-xl border-white/10 bg-white/[0.04] focus-visible:ring-primary" />
            </div>
            <div>
              <Label className="mb-1 block text-xs text-muted-foreground">Price (USD)</Label>
              <Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="299" className="rounded-xl border-white/10 bg-white/[0.04] focus-visible:ring-primary" />
            </div>
            <Button onClick={add} className="w-full rounded-xl bg-primary font-semibold text-primary-foreground hover:bg-primary/90">Add to store</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const avatar = user?.picture || user?.profile?.avatar || 'https://images.pexels.com/photos/8172091/pexels-photo-8172091.jpeg';
  const firstName = (user?.name || 'there').split(' ')[0];
  return (
    <div className="luxe-bg min-h-screen w-full">
      {/* Top bar */}
      <header className="glass-strong sticky top-0 z-30 border-b border-white/10">
        <div className="mx-auto flex max-w-6xl items-center gap-3 px-4 py-3">
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground"><Store className="h-4 w-4" /></span>
            <span className="font-display text-lg font-bold text-white">BioCloser</span>
          </div>
          <div className="ml-2 hidden items-center gap-2 sm:flex">
            <img src={avatar} alt={user?.name} className="h-7 w-7 rounded-full object-cover" />
            <span className="text-sm text-white/80">{user?.name}</span>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Button onClick={() => navigate('/onboarding')} size="sm" variant="ghost" className="rounded-full text-white/80 hover:bg-white/10 hover:text-white">
              <Package className="mr-1.5 h-4 w-4" /> Import
            </Button>
            <Button onClick={() => navigate('/demo')} size="sm" className="rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90 glow-lime">
              <MonitorPlay className="mr-1.5 h-4 w-4" /> Demo Mode
            </Button>
            <Link to={user?.handle ? `/${user.handle}` : '/store'}>
              <Button size="sm" variant="ghost" className="glass rounded-full text-white hover:bg-white/10">
                <Eye className="mr-1.5 h-4 w-4" /> View store
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-6">
        <div className="mb-5 animate-fade-up">
          <h1 className="font-display text-2xl font-bold text-white">Welcome back, {firstName} 👋</h1>
          <p className="text-sm text-muted-foreground">Here's how your store is performing.</p>
        </div>

        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="no-scrollbar mb-6 flex h-auto w-full justify-start gap-1 overflow-x-auto rounded-full bg-white/[0.04] p-1">
            {[
              { v: 'overview', l: 'Overview', icon: LayoutDashboard },
              { v: 'products', l: 'Products', icon: ShoppingBag },
              { v: 'leads', l: 'Leads', icon: Inbox },
              { v: 'concierge', l: 'AI Concierge', icon: Sparkles },
              { v: 'social', l: 'Social & Auto-DM', icon: MessageCircle },
              { v: 'billing', l: 'Billing', icon: CreditCard },
              { v: 'payouts', l: 'Payouts', icon: Landmark },
              { v: 'settings', l: 'Settings', icon: SettingsIcon },
            ].map((t) => (
              <TabsTrigger key={t.v} value={t.v} className="shrink-0 gap-1.5 rounded-full px-4 py-2 text-sm text-muted-foreground data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                <t.icon className="h-4 w-4" /> {t.l}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="overview"><Overview /></TabsContent>
          <TabsContent value="products"><ProductsTab /></TabsContent>
          <TabsContent value="leads"><LeadsTab /></TabsContent>
          <TabsContent value="concierge"><ConciergeTab /></TabsContent>
          <TabsContent value="social"><SocialTab /></TabsContent>
          <TabsContent value="billing"><BillingTab /></TabsContent>
          <TabsContent value="payouts"><PayoutsTab /></TabsContent>
          <TabsContent value="settings"><SettingsTab /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
